import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ScopeManager } from '../../features/auth/api/scope-manager';
import { AccountSelector } from '../../features/auth/ui/AccountSelector';
import { ScopeContextStorage } from '../../shared/lib/scope-storage';
import { prefetchProductBindings } from '../../features/auth/api/product-binding';
import type { ScopeContext, Account } from '../../features/auth/model/types';
import { DebugLogger } from '../../shared/lib/debug';
import { isFreshDocumentLoad } from '../../shared/lib/document-navigation';
import { exitToLogin } from '../../shared/lib/session-exit';
import { Logo } from '@/shared/ui/logo';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { useLanguage } from '@/shared/hooks/use-language';

/**
 * GET /account filters by holder_id, a non-ancestor Datastore query and therefore eventually
 * consistent — a just-created account can be absent from the very next listing. Merging it in
 * keeps the picker from telling the user an account was created and then not showing it.
 */
function withCreatedAccount(accounts: Account[], createdAccount?: Account): Account[] {
  if (!createdAccount || accounts.some(account => account.id === createdAccount.id)) {
    return accounts;
  }
  return [...accounts, createdAccount];
}

function AccountSelectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { t } = useLanguage();
  const sessionId = location.state?.sessionId;
  const passedScopeContext = location.state?.scopeContext;
  const createdAccount: Account | undefined = location.state?.createdAccount;
  
  const [arrivedBackwards] = useState(() => navigationType === 'POP' && !isFreshDocumentLoad());

  const [scopeContext, setScopeContext] = useState<ScopeContext>(passedScopeContext || {});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeManager, setScopeManager] = useState<ScopeManager | null>(null);
  const initializationRef = useRef(false);

  useEffect(() => {
    DebugLogger.debug('Account Selection page mounted', { 
      sessionId, 
      passedScopeContext,
      locationState: location.state 
    });
    
    // Prevent duplicate initialization in React StrictMode
    if (initializationRef.current) {
      DebugLogger.debug('Initialization already in progress, skipping');
      return;
    }
    
    if (!sessionId) {
      DebugLogger.error('No session ID found in location state', { 
        locationState: location.state,
        pathname: location.pathname 
      });
      setError(t("accountHolder.noSessionId"));
      setIsLoading(false);
      return;
    }

    // Check if we have a scope context (either passed or stored)
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId);
    
    // Allow accounts without holder context (user has direct account access)
    if (!contextToUse) {
      DebugLogger.debug('No scope context found, redirecting to account holder selection');
      navigate('/account-holder-selection', {
        state: { sessionId },
        replace: true
      });
      return;
    }

    // If we have a passed context (from account-holder-selection), it means accounts are available
    // even if not in the context object itself - don't redirect back
    // Only redirect if we got context from storage AND it needs holder selection AND has no account
    if (!passedScopeContext && ScopeContextStorage.needsAccountHolderSelection(contextToUse) && !contextToUse.account) {
      DebugLogger.debug('Account holder selection needed and no account in stored context, redirecting');
      navigate('/account-holder-selection', {
        state: { sessionId },
        replace: true
      });
      return;
    }

    const accountAlreadySelected = contextToUse.account && (
      contextToUse.accountHolder ?
        !ScopeContextStorage.needsAccountSelection(contextToUse) :
        true
    );

    if (accountAlreadySelected && !arrivedBackwards) {
      DebugLogger.debug('Account already selected, redirecting to console', {
        contextToUse
      });
      prefetchProductBindings(sessionId, contextToUse);
      navigate('/console', {
        state: { sessionId, scopeContext: contextToUse },
        replace: true
      });
      return;
    }

    DebugLogger.debug('Account selection needed, initializing scope', { 
      sessionId, 
      contextToUse 
    });
    
    setScopeContext(contextToUse);
    initializationRef.current = true;
    initializeScope(contextToUse);
  }, [sessionId, passedScopeContext, navigate, arrivedBackwards]);

  const initializeScope = async (context: ScopeContext) => {
    try {
      DebugLogger.debug('Starting scope initialization for account selection', { 
        sessionId, 
        context 
      });
      setIsLoading(true);
      setError(null);
      
      const scopeManager = new ScopeManager(sessionId!);
      
      // If we have a complete context with account holder, just load the accounts
      if (context.accountHolder) {
        DebugLogger.debug('Using passed account holder context, loading accounts only', { 
          holderId: context.accountHolder.id 
        });
        
        // Set the account holder in the scope manager's cache and context
        // This is needed so setAccount can find the holder later
        scopeManager.setAccountHolderInCache(context.accountHolder);
        
        // Load accounts for the existing holder without re-initializing everything
        const holderAccounts = withCreatedAccount(
          await scopeManager.getAccountsForHolder(context.accountHolder.id),
          createdAccount
        );
        scopeManager.setAccountsInCache(holderAccounts);
        setAccounts(holderAccounts);
        setScopeContext(context);
      } else if (!context.accountHolder) {
        // User has direct account access without holder context
        // Need to fetch all accounts without scope
        DebugLogger.debug('User has direct account access without holder, fetching all accounts', { 
          accountId: context.account?.id
        });
        
        // Import getAllAccounts to fetch accounts without scope
        const { getAllAccounts } = await import('../../features/auth/api/account');
        const accountsResponse = await getAllAccounts(sessionId!, 'active');
        const directAccounts = withCreatedAccount(accountsResponse.accounts, createdAccount);

        // Store accounts in scope manager's cache so setAccount can find them
        scopeManager.setAccountsInCache(directAccounts);

        setAccounts(directAccounts);
        
        // Update context - if we have multiple accounts, clear the auto-selected one so user can choose
        // If only one account, keep it selected (it should have been auto-selected already)
        const updatedContext: ScopeContext = {
          ...context,
          // If we have multiple accounts, clear the auto-selected one so user can choose
          account: directAccounts.length === 1 ? directAccounts[0] : undefined
        };
        setScopeContext(updatedContext);
      } else {
        // Fallback: full initialization if context is incomplete
        DebugLogger.debug('Context incomplete, performing full initialization');
        const initializedContext = await scopeManager.initializeScope();
        
        if (initializedContext.account || initializedContext.accountHolder) {
          DebugLogger.debug('Using initialized context', { 
            accountId: initializedContext.account?.id,
            holderId: initializedContext.accountHolder?.id
          });
          setAccounts(scopeManager.getAccounts());
          setScopeContext(initializedContext);
        } else {
          throw new Error('No accounts or organization found after initialization');
        }
      }
      
      setScopeManager(scopeManager);
      
    } catch (err) {
      DebugLogger.error('Scope initialization failed', { 
        sessionId, 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      setError(err instanceof Error ? err.message : 'Failed to initialize scope');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountSelect = async (account: Account) => {
    try {
      setIsLoading(true);
      
      if (!scopeManager) {
        throw new Error('Scope manager not initialized');
      }
      
      const context = await scopeManager.setAccount(account.id);
      
      DebugLogger.debug('Account selected', { 
        accountId: account.id, 
        accountName: account.name,
        context 
      });
      
      setScopeContext(context);
      
      // Store the updated context
      ScopeContextStorage.storeScopeContext(sessionId!, context);
      
      // Navigate to console
      DebugLogger.debug('Scope complete, navigating to console');
      prefetchProductBindings(sessionId!, context);
      navigate('/console', {
        state: { sessionId, scopeContext: context }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToAccountHolder = () => {
    DebugLogger.debug('Navigating back to account holder selection');
    navigate('/account-holder-selection', {
      state: { sessionId, reselect: true }
    });
  };

  // The direct-account user has neither a holder object nor, with more than one account, a
  // selected account to read a holder_id off — so both of the obvious sources are empty for
  // exactly the person the create entry point was widened for. The listed accounts still carry
  // one, and it is only unambiguous when they all agree; spanning holders, there is no single
  // answer and the entry point stays hidden rather than guessing.
  const listedHolderIds = new Set(accounts.map(account => account.holder_id).filter(Boolean));
  const holderId = scopeContext.accountHolder?.id
    ?? scopeContext.account?.holder_id
    ?? (listedHolderIds.size === 1 ? [...listedHolderIds][0] : undefined);

  const handleCreateAccount = () => {
    navigate('/account-creation', {
      state: { sessionId, scopeContext, holderId, origin: 'account-selection' }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-4">
        <div className="w-full max-w-2xl text-center">
          <Logo className="justify-center mb-4" />
          <p className="text-muted-foreground">{t("account.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-4">
        <div className="w-full max-w-2xl">
          <Logo className="justify-center mb-8" />
          <Card className="p-6 border-destructive">
            <p className="text-destructive mb-4">
              <strong>{t("account.error")}</strong> {error}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleBackToAccountHolder}>
                {t("account.backToHolderSelection")}
              </Button>
              <Button variant="outline" onClick={exitToLogin}>
                {t("account.backToLogin")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AccountSelector
      accounts={accounts}
      selectedAccount={scopeContext.account || null}
      onSelect={handleAccountSelect}
      onBack={scopeContext.accountHolder ? handleBackToAccountHolder : undefined}
      onCreateAccount={holderId ? handleCreateAccount : undefined}
      onExit={exitToLogin}
      isLoading={isLoading}
    />
  );
}

export default AccountSelectionPage;
